/**
 * PromoterDashboardRewardsByAmount.tsx - Amount-Based Rewards Display
 *
 * This component displays amount-based rewards with the promoter's progress
 * toward achieving each reward tier based on total donations raised.
 *
 * Key Features:
 * - Amount-based reward tiers with progress tracking
 * - Visual progress indicators for each reward level
 * - Achievement status and completion celebration
 * - Next reward target identification and motivation
 * - Reward descriptions and value information
 * - Progress visualization with clear milestones
 *
 * Reward Tier Display:
 * - Reward amount thresholds with clear targets
 * - Progress bars showing current vs required amounts
 * - Achievement status (completed, in progress, locked)
 * - Reward descriptions and benefits
 * - Visual indicators for completed rewards
 *
 * Progress Visualization:
 * - Current amount raised vs reward requirements
 * - Percentage completion for each tier
 * - Distance to next reward achievement
 * - Visual celebration for completed rewards
 * - Motivational messaging for next steps
 *
 * Achievement Tracking:
 * - Completed reward celebration and recognition
 * - In-progress rewards with clear targets
 * - Locked rewards with requirements
 * - Achievement timeline and history
 *
 * Props:
 * - promoterId: string - ID of promoter for reward tracking
 * - currentAmount: number - Current amount raised by promoter
 *
 * Dependencies: MobX store, reward calculations, progress utilities
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Trophy, Check, Lock } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { RewardModel } from "../../models/FundraisersGridModels";
import { PromoterProgressModel } from "../../models";

const NextRewardPanel = styled.div`
  background: #f0fdf4;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px 32px 32px 32px;
  margin-bottom: 43px;
`;

const NextRewardContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
`;

const NextRewardLeft = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 16px;
`;

const NextRewardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;

const NextRewardLabel = styled.div`
  font-size: 19px;
  font-weight: 500;
  color: #10b981;
  margin: 0;
`;

const NextRewardTitle = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
  margin: 0;
`;

const UnlockBadge = styled.div`
  background: #10b981;
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 17px;
  font-weight: 500;
  white-space: nowrap;
  align-self: flex-start;
`;

const ProgressSection = styled.div`
  margin-top: 32px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressBarFill = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: #10b981;
  border-radius: 4px;
  transition: width 1.5s ease-out;
`;

const ProgressLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressAmount = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
`;

const RewardTrack = styled.div`
  margin-top: 32px;
  position: relative;
`;

const ScrollableTrackContainer = styled.div<{ hasScroll: boolean }>`
  overflow-x: ${(props) => (props.hasScroll ? "auto" : "visible")};
  padding-bottom: ${(props) => (props.hasScroll ? "16px" : "0")};
  margin: 0 24px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const TrackContainer = styled.div<{ minWidth: number }>`
  position: relative;
  min-width: ${(props) => props.minWidth}px;
`;

const TrackRail = styled.div<{ startX: number; endX: number }>`
  position: absolute;
  left: ${(props) => props.startX}px;
  right: ${(props) => `calc(100% - ${props.endX}px)`};
  top: 28px;
  height: 4px;
  background: #e2e8f0;
  z-index: 1;
`;

const GreenTrackRail = styled.div<{
  startX: number;
  endX: number;
  visible: boolean;
}>`
  position: absolute;
  left: ${(props) => props.startX}px;
  right: ${(props) => `calc(100% - ${props.endX}px)`};
  top: 28px;
  height: 4px;
  background: #bbf7d0;
  z-index: 2;
  display: ${(props) => (props.visible ? "block" : "none")};
`;

const RewardsGrid = styled.div<{ columns: number; minWidth: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns}, 1fr)`};
  gap: 16px;
  position: relative;
  z-index: 2;
  min-width: ${(props) => props.minWidth}px;
`;

const RewardItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
`;

const RewardIcon = styled.div<{ isEarned: boolean; isNext: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  transition: all 0.2s ease;
  position: relative;

  ${(props) => {
    if (props.isEarned) {
      return `
        background: #10b981;
        border-color: #10b981;
        color: #ffffff;
      `;
    } else if (props.isNext) {
      return `
        background: #d1fae5;
        border-color: #10b981;
        color: #10b981;
      `;
    } else {
      return `
        background: #f1f5f9;
        border-color: #9ca3af;
        color: #555555;
      `;
    }
  }}
`;

const CheckIcon = styled(Check)`
  width: 30px;
  height: 30px;
  color: #ffffff;
`;

const RewardAmount = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
`;

const RewardName = styled.div`
  font-size: 14px;
  color: #64748b;
  line-height: 1.3;
  font-weight: 500;
`;

interface PromoterDashboardRewardsByAmountProps {
  promoterId: string;
  fundraiserId: string;
  rewards: RewardModel[];
  promoter: PromoterProgressModel;
}

export const PromoterDashboardRewardsByAmount: React.FC<PromoterDashboardRewardsByAmountProps> =
  observer(({ promoterId, fundraiserId, rewards, promoter }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [hasScrolled, setHasScrolled] = useState(false);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const promoterRaised = promoter.totalRaisedAmount;

    // Level-based reward logic
    const nextLevelReward = rewards.find(
      (reward) => reward.level > promoterRaised
    );
    const levelToUnlock = nextLevelReward
      ? nextLevelReward.level - promoterRaised
      : 0;
    const allLevelRewardsAchieved = rewards.length > 0 && !nextLevelReward;

    // Calculate progress for level-based rewards
    const getLevelProgressToNext = () => {
      if (!nextLevelReward) return 100; // All rewards achieved

      const previousReward = rewards
        .filter((r) => r.level <= promoterRaised)
        .sort((a, b) => b.level - a.level)[0];

      const startAmount = previousReward ? previousReward.level : 0;
      const endAmount = nextLevelReward.level;
      const currentProgress = promoterRaised - startAmount;
      const totalRange = endAmount - startAmount;

      return Math.min((currentProgress / totalRange) * 100, 100);
    };

    const levelProgressPercentage = getLevelProgressToNext();

    // Determine if we need horizontal scroll
    const hasLevelScroll = rewards.length > 4;
    const minLevelTrackWidth = hasLevelScroll ? rewards.length * 150 : 0;

    // Auto-scroll to show current achievement and next goal on page load
    useEffect(() => {
      if (hasLevelScroll && scrollContainerRef.current && !hasScrolled) {
        const currentIndex = rewards.findIndex((r) => r.level > promoterRaised);
        if (currentIndex > 0) {
          const scrollPosition = Math.max(0, (currentIndex - 1) * 150);
          scrollContainerRef.current.scrollLeft = scrollPosition;
          setHasScrolled(true);
        }
      }
    }, [rewards, promoterRaised, hasLevelScroll, hasScrolled]);

    // Format reward names to handle multi-line display
    const formatRewardName = (reward: string) => {
      const parts = reward.split(/[\+&]/);
      return parts.length > 1 ? parts : [reward];
    };

    // Calculate track rail positioning for level rewards
    const getTrackRailPositioning = () => {
      if (rewards.length === 0) return { startX: 0, endX: 0, greenEndX: 0 };

      const itemWidth = hasLevelScroll ? 150 : 100 / rewards.length;
      const iconSize = 28; // Half of the 56px icon size for centering

      // Find the last earned reward index
      const lastEarnedIndex =
        rewards.findIndex((r) => r.level > promoterRaised) - 1;

      if (hasLevelScroll) {
        const greenEndX =
          lastEarnedIndex >= 0
            ? (lastEarnedIndex + 0.5) * itemWidth + iconSize / 2
            : itemWidth / 2 - iconSize / 2; // If no rewards earned, green line doesn't show

        return {
          startX: itemWidth / 2 - iconSize / 2,
          endX: (rewards.length - 0.5) * itemWidth + iconSize / 2,
          greenEndX,
        };
      } else {
        // For non-scrolling layout
        const greenEndX =
          lastEarnedIndex >= 0
            ? ((lastEarnedIndex + 0.5) / rewards.length) * 100
            : (0.5 / rewards.length) * 100;

        return {
          startX: 100 / rewards.length / 2,
          endX: 100 - 100 / rewards.length / 2,
          greenEndX,
        };
      }
    };

    const { startX, endX, greenEndX } = getTrackRailPositioning();

    return (
      <>
        {nextLevelReward && !allLevelRewardsAchieved && (
          <NextRewardPanel>
            <NextRewardContent>
              <NextRewardLeft>
                <NextRewardRow>
                  <NextRewardLabel>Next reward</NextRewardLabel>
                </NextRewardRow>
                <NextRewardTitle style={{ marginTop: "-4px" }}>
                  {nextLevelReward.reward}
                </NextRewardTitle>
              </NextRewardLeft>
              <UnlockBadge>
                {formatCurrency(levelToUnlock)} to unlock
              </UnlockBadge>
            </NextRewardContent>

            <ProgressSection>
              <ProgressBarContainer>
                <ProgressBarFill percentage={levelProgressPercentage} />
              </ProgressBarContainer>
              <ProgressLabels>
                <ProgressAmount>
                  {formatCurrency(promoterRaised)}
                </ProgressAmount>
                <ProgressAmount>
                  {formatCurrency(nextLevelReward.level)}
                </ProgressAmount>
              </ProgressLabels>
            </ProgressSection>
          </NextRewardPanel>
        )}

        <RewardTrack>
          <ScrollableTrackContainer
            ref={scrollContainerRef}
            hasScroll={hasLevelScroll}
          >
            <TrackContainer minWidth={minLevelTrackWidth}>
              <TrackRail
                startX={hasLevelScroll ? 75 : 0}
                endX={hasLevelScroll ? minLevelTrackWidth - 75 : 0}
                style={{
                  left: hasLevelScroll ? "75px" : `${startX}%`,
                  right: hasLevelScroll ? "75px" : `${100 - endX}%`,
                }}
              />

              {/* Green track rail for earned rewards */}
              <GreenTrackRail
                startX={hasLevelScroll ? 75 : 0}
                endX={hasLevelScroll ? minLevelTrackWidth - greenEndX : 0}
                visible={promoterRaised >= rewards[0]?.level}
                style={{
                  left: hasLevelScroll ? "75px" : `${startX}%`,
                  right: hasLevelScroll
                    ? `${minLevelTrackWidth - greenEndX}px`
                    : `${100 - greenEndX}%`,
                }}
              />

              <RewardsGrid
                columns={rewards.length}
                minWidth={minLevelTrackWidth}
              >
                {rewards.map((reward, index) => {
                  const isEarned = reward.level <= promoterRaised;
                  const isNext = reward.level === nextLevelReward?.level;
                  const isFuture = reward.level > (nextLevelReward?.level || 0);
                  const rewardNameParts = formatRewardName(reward.reward);

                  return (
                    <RewardItem key={reward.id}>
                      <RewardIcon isEarned={isEarned} isNext={isNext}>
                        {isEarned ? (
                          <CheckIcon />
                        ) : isNext ? (
                          <Trophy size={24} />
                        ) : (
                          <Lock size={24} />
                        )}
                      </RewardIcon>

                      <div>
                        <RewardAmount>
                          {formatCurrency(reward.level)}
                        </RewardAmount>
                        <RewardName>
                          {rewardNameParts.map((part, partIndex) => (
                            <div key={partIndex}>{part.trim()}</div>
                          ))}
                        </RewardName>
                      </div>
                    </RewardItem>
                  );
                })}
              </RewardsGrid>
            </TrackContainer>
          </ScrollableTrackContainer>
        </RewardTrack>
      </>
    );
  });

// Helper function for formatting reward names
const formatRewardName = (reward: string) => {
  const parts = reward.split(/[\+&]/);
  return parts.length > 1 ? parts : [reward];
};
